import { AgGridReact } from "ag-grid-react";
import { useEffect, useMemo, useState } from "react";
import images from "../../assets/images";
import { themeQuartz } from "ag-grid-community";
import { Trash2 } from "lucide-react";
import { useDepartmentStore } from "../../store/store";
import { departmentObjType } from "../../types/api";
import { usePermissions } from "../../hooks/usePermission";
import { CREATE, DELETE, DEPARTMENTS, READ, UPDATE } from "../../constants/modules";

const gridTheme = themeQuartz.withParams({
  backgroundColor: "#fff",
  foregroundColor: "#2a2a2a",
  headerTextColor: "#2a2a2a",
  headerBackgroundColor: "#E9F5FF",
  oddRowBackgroundColor: "rgb(0, 0, 0, 0.03)",
  headerColumnResizeHandleColor: "rgb(126, 46, 132)",
  fontSize: "0.75rem",
  headerFontWeight: "bold",
});

const Departments: React.FC = () => {
  const [rowData, setRowData] = useState<departmentObjType[]>([]);
  const [originalData, setOriginalData] = useState<departmentObjType[]>([]);

  const [formData, setFormData] = useState({
    departmentId: "",
    departmentName: "",
  });

  const [editId, setEditId] = useState<string | null>(null);
  const [errors, setErrors] = useState<string | boolean>(false);
  const {
    departments,
    loading,
    fetchAllDepartments,
    updateDepartment,
    createDepartment,
    deleteDepartment,
  } = useDepartmentStore();

  const { withPermission, hasPermission } = usePermissions();
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    const resData = departments?.map((department: any) => ({
      id: department._id,
      departmentName: department.departmentName,
      departmentId: department.departmentId,
      action: null,
    }));
    setRowData(resData);
    setOriginalData(resData);
  }, [departments]);

  useEffect(() => {
    // if (hasPermission(DEPARTMENTS, READ)) {
    //   setAllowed(true);
    fetchAllDepartments();
    // }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrors(false);
  };

  const handleEdit = (data: departmentObjType) => {
    // withPermission(DEPARTMENTS, UPDATE, async () => {
    setFormData({
      departmentId: data.departmentId,
      departmentName: data.departmentName,
    });
    setEditId(data.id);
    setErrors(false);
    // });
  };

  const handleDelete = async (id: string) => {
    // withPermission(DEPARTMENTS, DELETE, async () => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this department?"
    );

    if (confirmed) {
      await deleteDepartment(id);
      //   await fetchAllDepartments();
    }
    // });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.departmentId === "" || formData.departmentName === "") {
      setErrors(true);
      return;
    }

    if (editId) {
      // withPermission(DEPARTMENTS, UPDATE, async () => {
      await updateDepartment(editId, formData);
      // });
    } else {
      // withPermission(DEPARTMENTS, CREATE, async () => {
      await createDepartment(formData);
      // });
    }

    // await fetchAllDepartments();
    setFormData({ departmentId: "", departmentName: "" });
    setEditId(null);
  };

  const searchData = async (keyword: any) => {
    if (!keyword.trim()) {
      setRowData(originalData);
      return;
    }

    const filteredData = originalData.filter((row) =>
      Object.values(row).some((value) =>
        String(value).toLowerCase().includes(keyword.toLowerCase())
      )
    );
    setRowData(filteredData);
  };

  const [columnDefs, setColDefs] = useState<any>([
    {
      field: "departmentId",
      headerName: "Department ID",
      sortable: false,
      // minWidth: 20,
      minWidth: 30,
      maxWidth: 150,
      flex: 0,
      headerStyle: {
        backgroundColor: "#e9f5ff",
      },
      filter: false,
      cellStyle: {
        textAlign: "left",
        padding: "10px 0 0 15px",
      },
    },
    {
      field: "departmentName",
      headerName: "Department Name",
      sortable: false,
      minWidth: 20,
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      filter: false,
      cellStyle: {
        padding: "10px 5px 0 10px",
        textAlign: "left",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
      tooltipField: "departmentName",
    },
    {
      field: "action",
      headerName: "Action",
      cellStyle: { padding: "10px 15px 0 0 ", textAlign: "left" },
      // minWidth: 20,
      headerStyle: { backgroundColor: "#e9f5ff" },
      flex: 0,
      minWidth: 40,
      maxWidth: 90,
      cellRenderer: (params: any) => (
        <div className="flex gap-x-3 pt-2">
          <button
            onClick={() => handleEdit(params.data)}
            className="p-1 hover:bg-gray-100 rounded"
            title="Edit Department"
          >
            <img className="iconTd w-5 h-5" src={images.editIcon} alt="edit" />
          </button>
          <button
            onClick={() => handleDelete(params.data.id)}
            className="p-1 hover:bg-red-100 rounded"
            title="Delete Department"
          >
            <Trash2 className="text-red-500" size={23} />
          </button>
        </div>
      ),
      filter: false,
    },
  ]);

  const defaultColDef = useMemo(() => {
    return {
      editable: false,
      filter: false,
      tooltipComponentParams: {
        color: "#ececec",
      },
    };
  }, []);
  const theme = useMemo(() => {
    return gridTheme;
  }, []);

  // if (!allowed)
  //   return (
  //     <>
  //       <div className="flex flex-col align-items-center py-5 my-5 min-h-screen">
  //         <h3 className="font-semibold text-gray-500 pt-4 pr-4">
  //           Access denied.
  //         </h3>
  //       </div>
  //     </>
  //   );

  return (
    <>
      <div className="p-3 mt-14 min-h-screen">
        <div className="flex justify-between items-center mb-4">
          <h2 className="ml-2">Departments</h2>
          <div className="rightTableSearchBlock w-25">
            <div className="input-affix">
              <i className="prefix-icon anticon anticon-search text-lg"></i>
              <input
                type="text"
                className="form-control"
                onChange={(e) => searchData(e.target.value)}
                placeholder="Search"
              />
            </div>
          </div>
        </div>

        <div className="tableBlock w-full d-flex justify-content-between">
          {/* grid grid-cols-2 */}
          <div className="card w-60">
            {rowData?.length > 0 ? (
              <div className="ag-theme-alpine w-full min-h-[400px] border-0 rounded-lg shadow-md">
                <AgGridReact
                  rowData={rowData}
                  columnDefs={columnDefs}
                  theme={theme}
                  loading={loading}
                  headerHeight={65}
                  rowHeight={50}
                  defaultColDef={defaultColDef}
                // domLayout="autoHeight"
                />
              </div>
            ) : (
              <div className="flex flex-col justify-center items-center min-h-[350px] mb-5">
                <p className="text-gray-500 text-lg mb-4">
                  No Departments added yet
                </p>
                {/* <button className="font-semibold bg-[#0fa9a6] text-white px-4 py-2 rounded-full transition-colors">
                  Add Department
                </button> */}
              </div>
            )}
          </div>

          {/* // adding Department */}
          <div className="card w-35  max-h-[320px] drop-shadow-lg rounded-lg p-6 flex-col">
            <h4 className="text-[#375998] text-xl font-semibold mb-6">
              {editId ? "Edit Department" : "Create Department"}
            </h4>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="flex flex-col">
                <label className="text-sm font-medium mb-1">
                  Department ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className={`border border-gray-300 rounded-md ml-1 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#375998]`}
                  placeholder="Enter Department ID"
                  name="departmentId"
                  value={formData.departmentId}
                  onChange={handleChange}
                />
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-medium  mb-1">
                  Department Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className={`border border-gray-300 rounded-md ml-1 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#375998]
                    `}
                  placeholder="Enter Department Name"
                  name="departmentName"
                  value={formData.departmentName}
                  onChange={handleChange}
                />
              </div>
              {errors === true ? (
                <p className="text-red-500 text-sm mt-2">
                  Fill all required fields
                </p>
              ) : (
                ""
              )}

              <div className="flex justify-end pt-1 gap-6">
                <button
                  type="button"
                  onClick={() => {
                    setEditId(null);
                    setFormData({ departmentId: "", departmentName: "" });
                    setErrors(false);
                  }}
                  className="px-4 py-2 bg-[#869ab6] text-white rounded-full font-semi-bold hover:opacity-90"
                >
                  CANCEL
                </button>
                <button
                  type="submit"
                  className="bg-[#0fa9a6] text-white text-md px-4 py-2 rounded-full font-semi-bold hover:opacity-90"
                >
                  {editId ? "UPDATE" : "SUBMIT"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default Departments;
